import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, User, Home, MapPin, Calendar, FileText, Euro, TrendingUp, TrendingDown, Banknote, Award, CheckCircle2, ArrowRight, Layers, Info, CalendarPlus, FolderPlus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { getUserRole } from "@/pages/Login";
import { buildTimelinesForProgram, TimelineDisplay, CashFlowTable } from "@/components/ProjectTimeline";
import { computeSteps, getSecondProgramSteps, MPR_TIMELINE_STEPS, TIMELINE_174_STEPS, formatCurrency, formatDateTime, PROFIL_LABELS, PRECA_LABELS, GESTES_LABELS, EQUIP_LABELS, DOCS_PROFIL, DOCS_PROG, REGIE_THRESHOLD } from "@/utils/simulateurUtils";
import { RdvAdminModal } from "@/components/RdvAdminModal";
import { toast } from "sonner";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const fmtC = formatCurrency;
const fmtDT = formatDateTime;
const PROFIL_L = PROFIL_LABELS;
const PRECA_L = PRECA_LABELS;
const GESTES_L = GESTES_LABELS;
const EQUIP_L = EQUIP_LABELS;

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-border/30">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn("text-xs", bold ? "font-bold" : "font-semibold")}>{value || "-"}</span>
    </div>
  );
}

export default function EtudeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [etude, setEtude] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRdvModal, setShowRdvModal] = useState(false);
  const [creatingDossier, setCreatingDossier] = useState(false);
  const isAdmin = getUserRole() === "admin_full";
  const isRegie = !isAdmin;

  useEffect(() => {
    fetch(`${API}/etudes/${id}`).then(r => r.json()).then(setEtude).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-12 text-muted-foreground">Chargement...</div>;
  if (!etude) return <div className="text-center py-12 text-red-500">Étude non trouvée</div>;

  const b = etude.beneficiaire || {};

  const handleCreateDossierFromEtude = async () => {
    setCreatingDossier(true);
    try {
      const payload = { ...etude, type: "dossier", status: "rdv_admin", savedByRegie: true };
      delete payload.id;
      delete payload._id;
      const res = await fetch(`${API}/etudes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json();
      toast.success("Dossier créé avec succès !");
      navigate(`/dossier-regie/${data.id}`);
    } catch (err) { toast.error("Erreur lors de la création du dossier"); }
    setCreatingDossier(false);
  };

  const regies = etude.regies || {};
  const res174 = etude.results174 || [];
  const resMPR = etude.allMPRResults || [];
  const a174 = etude.active174Result;
  const aMPR = etude.activeMPRResult;
  const a174C = etude.active174CumResult;
  const aMPRC = etude.activeMPRCumResult;
  const rec = etude.recommendation;

  // ═══ Reconstruct admin recommendation boxes ═══
  const adminOptions = [];
  if (etude.eligCumule?.eligible && a174C && aMPRC) {
    adminOptions.push({ type: "cumule", label: `Op. Cumulée (${(etude.eligCumule.order || []).join(" puis ")})`, ca: (a174C.caHT || 0) + (aMPRC.totalAides || aMPRC.mpr + aMPRC.cee || 0), marge: (a174C.marge || 0) + (aMPRC.marge || 0) });
  }
  if (etude.elig174?.eligible && a174) {
    adminOptions.push({ type: "174", label: "Programme 174", ca: a174.caHT || 0, marge: a174.marge || 0 });
  }
  if (etude.eligMPR?.eligible && aMPR) {
    adminOptions.push({ type: "mpr", label: `Programme MPR (${etude.activeMPRType || "PAC/SSC"})`, ca: aMPR.totalAides || 0, marge: aMPR.marge || 0 });
  }
  adminOptions.sort((a, b) => b.marge - a.marge);

  // ═══ Reconstruct régie view data ═══
  const THRESHOLD = REGIE_THRESHOLD;
  const regie174OK = etude.elig174?.eligible && a174 && a174.marge >= THRESHOLD;
  const regieMPROK = etude.eligMPR?.eligible && aMPR && aMPR.marge >= THRESHOLD;
  const regieCumOK = etude.eligCumule?.eligible && a174C && aMPRC && ((a174C.marge || 0) + (aMPRC.marge || 0)) >= THRESHOLD;

  const regieOpts = [];
  if (regie174OK) regieOpts.push({ type: "174", label: "Programme 174", earnings: regies.r174 || 0, margin: a174.marge });
  if (regieMPROK) regieOpts.push({ type: "mpr", label: `Programme MPR (${etude.activeMPRType || ""})`, earnings: regies.rMPR || 0, margin: aMPR.marge });
  if (regieCumOK) regieOpts.push({ type: "cumule", label: `Op. Cumulée (${(etude.eligCumule?.order || []).join(" puis ")})`, earnings: (regies.r174Cum || 0) + (regies.rMPRCum || 0), margin: (a174C?.marge || 0) + (aMPRC?.marge || 0) });
  regieOpts.sort((a, b) => b.margin - a.margin);

  const regieBest = regieOpts.length > 0 ? regieOpts[0].type : null;
  const regie174Sc = regie174OK ? res174.filter(r => r.result.marge >= THRESHOLD) : [];
  const regieMPRSc = regieMPROK ? resMPR.filter(r => r.result.marge >= THRESHOLD) : [];

  // Reconstruct payment boxes
  const pBoxes = etude.paymentBoxes || [];

  // Reconstruct timeline
  const buildTL = (detail) => {
    const eligCum = etude.eligCumule?.eligible ? { order: etude.eligCumule.order } : null;
    return buildTimelinesForProgram({ programType: detail, eligCumule: eligCum, elig174: etude.elig174 || {}, eligMPR: etude.eligMPR || {} });
  };

  // Documents
  const profilDocs = DOCS_PROFIL[etude.profil] || [];
  const progDocs = [];
  if (regieBest === "cumule" || regieBest === "174") progDocs.push(...(DOCS_PROG["174"] || []));
  if (regieBest === "cumule" || regieBest === "mpr") progDocs.push(...(DOCS_PROG.MPR || []));
  const allDocs = [...new Set([...profilDocs, ...progDocs])];

  // Timeline for admin (based on recommended)
  const adminDetail = adminOptions.length > 0 ? adminOptions[0].type : null;
  const adminTL = adminDetail ? buildTL(adminDetail) : { timelines: [], totalDays: 0 };
  // Timeline for régie
  const regieTL = regieBest ? buildTL(regieBest) : { timelines: [], totalDays: 0 };

  // ═══ SHARED: Render info tab ═══
  const renderInfoTab = () => (
    <div className="space-y-4">
      {b.nom && (
        <div className="border border-border rounded-xl bg-card p-4">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><User className="w-4 h-4 text-orange-500" /> Bénéficiaire</h3>
          <Row label="Nom" value={b.nom} bold />
          <Row label="Téléphone" value={b.telephone} />
          <Row label="Email" value={b.email} />
          <Row label="Adresse" value={b.adresse} />
        </div>
      )}
      <div className="border border-border rounded-xl bg-card p-4">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Home className="w-4 h-4 text-orange-500" /> Profil & Logement</h3>
        <Row label="Profil" value={PROFIL_L[etude.profil] || etude.profil} />
        <Row label="Période" value={etude.periode === "from_jan_2026" ? "À partir de janvier 2026" : "Avant janvier 2026"} />
        <Row label="Type" value={etude.typeBatiment === "maison" ? "Maison" : "Appartement"} />
        <Row label="Surface" value={`${etude.surface} m²`} />
        <Row label="Zone" value={etude.zone} />
        <Row label="Chauffage" value={etude.chauffage} />
        <Row label="Niveaux" value={etude.niveaux} />
      </div>
      <div className="border border-border rounded-xl bg-card p-4">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-orange-500" /> DPE & Précarité</h3>
        <Row label="DPE actuel" value={etude.dpeActuel} />
        <Row label="Âge logement" value={etude.ageLogement} />
        <Row label="Précarité" value={PRECA_L[etude.precarite] || etude.precarite} />
        <Row label="Nb personnes" value={etude.nbPersonnes} />
        <Row label="Revenu fiscal" value={fmtC(etude.revenuFiscal)} />
        <Row label="Région fiscale" value={etude.regionFiscale} />
      </div>
    </div>
  );

  // ═══ SHARED: Render régie view tab ═══
  const renderRegieTab = () => (
    <div className="space-y-5">
      {regieOpts.length === 0 ? (
        <div className="border-2 border-red-300 rounded-xl bg-red-500/10 p-6 text-center">
          <p className="font-bold text-red-700">Aucun programme éligible pour la régie</p>
        </div>
      ) : (
        <>
          {/* Programs with earnings */}
          <div className="border-2 border-orange-400 rounded-xl bg-orange-50 p-5">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-4"><Award className="w-4 h-4 text-orange-500" /> Programmes éligibles</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {regieOpts.map((opt, i) => (
                <div key={opt.type} className={cn("p-4 rounded-xl border-2 bg-card shadow-sm", i === 0 ? "border-emerald-400 ring-2 ring-emerald-200" : "border-border")}>
                  {i === 0 && <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1">Recommandé</p>}
                  <p className="font-semibold text-sm">{opt.label}</p>
                  <p className="text-lg font-extrabold text-orange-600 mt-2">{fmtC(opt.earnings)}</p>
                  <p className="text-[10px] text-muted-foreground">Commission régie totale</p>
                </div>
              ))}
            </div>
            {regieMPRSc.length > 0 && (
              <div className="mt-4 pt-3 border-t border-orange-200">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Scénarios MPR éligibles :</p>
                <div className="flex gap-2 flex-wrap">
                  {regieMPRSc.map(r => <span key={r.type} className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold border border-blue-500/30">{r.type}</span>)}
                </div>
              </div>
            )}
            {regie174Sc.length > 0 && (
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Scénarios 174 éligibles :</p>
                <div className="space-y-2">
                  {regie174Sc.map((r, i) => (
                    <div key={i} className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                      <span className="text-xs font-bold text-emerald-700">{r.scenario.label}</span>
                      <p className="text-[10px] text-emerald-600 mt-0.5">
                        {r.scenario.sauts} sauts — {(r.scenario.gestes || []).map(g => GESTES_L[g] || g).join(" + ")}
                        {(r.scenario.equipements || []).length > 0 && ` + ${r.scenario.equipements.map(e => EQUIP_L[e] || e).join(", ")}`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Timeline */}
          {regieTL.timelines.length > 0 && <TimelineDisplay timelines={regieTL.timelines} totalDays={regieTL.totalDays} />}

          {/* Payment boxes */}
          {pBoxes.length > 0 && (
            <div className={cn("grid gap-3", pBoxes.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 max-w-sm")}>
              {pBoxes.map((box, i) => (
                <div key={i} className={cn("rounded-xl border-2 p-4 text-center shadow-md", box.prog === "174" ? "border-emerald-400 bg-emerald-500/10" : "border-blue-400 bg-blue-500/10")}>
                  <Banknote className={cn("w-7 h-7 mx-auto mb-1.5", box.prog === "174" ? "text-emerald-600" : "text-blue-600")} />
                  <p className={cn("text-[10px] font-bold uppercase tracking-wide mb-1", box.prog === "174" ? "text-emerald-700" : "text-blue-700")}>{box.label}</p>
                  <p className={cn("text-2xl font-extrabold", box.prog === "174" ? "text-emerald-700" : "text-blue-700")}>{fmtC(box.amount)}</p>
                  <p className="text-xs font-semibold text-muted-foreground mt-1">J{box.day}</p>
                </div>
              ))}
            </div>
          )}

          {/* Documents */}
          {allDocs.length > 0 && (
            <div className="border border-border rounded-xl bg-card p-5">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Layers className="w-4 h-4 text-orange-500" /> Documents à demander</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {allDocs.map((doc, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1"><CheckCircle2 className="w-3.5 h-3.5 text-orange-400 shrink-0" /><span>{doc}</span></div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ═══ ADMIN: Render full financial analysis tab ═══
  const renderFinanceTab = () => (
    <div className="space-y-5">
      {/* Paramètres du calcul */}
      <div className="border border-border rounded-xl bg-card p-4">
        <h3 className="text-xs font-semibold text-muted-foreground mb-3">Paramètres du calcul</h3>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span>Valorisation CEE 174 : <strong>{etude.valorisation || 13} €/MWh</strong></span>
          <span className={cn("px-2.5 py-1 rounded-full font-bold text-[10px]", etude.regieMode === "regie" ? "bg-orange-500/20 text-orange-600 dark:text-orange-400" : "bg-blue-500/20 text-blue-600 dark:text-blue-400")}>
            {etude.regieMode === "regie" ? "Régie" : "Lead"}
          </span>
          <span>174: <strong>{fmtC(regies.r174)}</strong> MPR: <strong>{fmtC(regies.rMPR)}</strong></span>
          <span>Cum. 174: <strong>{fmtC(regies.r174Cum)}</strong> Cum. MPR: <strong>{fmtC(regies.rMPRCum)}</strong></span>
          <span className="flex items-center gap-1.5">
            <span className={cn("w-2.5 h-2.5 rounded-full", etude.precarite === "bleu" ? "bg-blue-500" : "bg-yellow-500/100")} />
            <strong>{PRECA_L[etude.precarite] || etude.precarite}</strong>
          </span>
        </div>
      </div>

      {/* Recommendation boxes */}
      {adminOptions.length > 0 && (
        <div className="border-2 border-orange-400 rounded-xl bg-orange-50 p-5">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-orange-500" /> Recommandation
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {adminOptions.map((opt, i) => (
              <div key={opt.type} className={cn("p-4 rounded-xl border-2 bg-card shadow-sm", i === 0 ? "border-orange-400 ring-2 ring-orange-200" : "border-border")}>
                {i === 0 && <p className="text-[10px] font-bold text-red-600 uppercase mb-1">LE PLUS RENTABLE</p>}
                <p className="font-semibold text-sm mb-3">{opt.label}</p>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">CA</span>
                  <span className="font-bold">{fmtC(opt.ca)}</span>
                </div>
                <div className="flex justify-between items-center text-xs mt-1">
                  <span className="text-muted-foreground">Marge</span>
                  <span className={cn("font-bold", opt.marge >= REGIE_THRESHOLD ? "text-emerald-600" : "text-red-500")}>{fmtC(opt.marge)}</span>
                </div>
                {i === 0 && <div className="h-1 bg-orange-500 rounded-full mt-3" />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Eligibility cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {etude.elig174?.eligible && (
          <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <div><p className="text-xs font-bold text-emerald-700">Programme 174</p><p className="text-[10px] text-emerald-600">Bailleur SCI : toujours éligible.</p></div>
          </div>
        )}
        {etude.eligMPR?.eligible && (
          <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <div><p className="text-xs font-bold text-emerald-700">Programme MPR</p><p className="text-[10px] text-emerald-600">Éligible au MPR (maison + chauffage gaz/fioul).</p></div>
          </div>
        )}
        {etude.eligCumule?.eligible && (
          <div className="p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <div><p className="text-xs font-bold text-emerald-700">Op. Cumulée</p><p className="text-[10px] text-emerald-600">Ordre : {(etude.eligCumule.order || []).join(" puis ")}</p></div>
          </div>
        )}
      </div>

      {/* Ordre du montage */}
      {etude.eligCumule?.eligible && etude.eligCumule.order && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-violet-500/10 border border-violet-500/30">
          <ArrowRight className="w-4 h-4 text-violet-500" />
          <span className="text-xs font-semibold text-violet-700">Ordre du montage Op. Cumulée : {etude.eligCumule.order.map((p, i) => (
            <span key={p}>{i > 0 && " → "}<strong>Programme {p}</strong></span>
          ))}</span>
        </div>
      )}

      {/* Programme 174 Scénarios */}
      {res174.length > 0 && (
        <div className="border border-border rounded-xl bg-card p-4">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-emerald-500" /> Programme 174 — Scénarios
            <span className="text-[10px] text-muted-foreground font-normal">(régie {adminOptions[0]?.type === "cumule" ? "cumulée" : ""}: {fmtC(adminOptions[0]?.type === "cumule" ? regies.r174Cum : regies.r174)})</span>
          </h3>
          <div className="space-y-3 mt-3">
            {res174.map((r, i) => {
              const isBest = i === 0 || r.result.marge === Math.max(...res174.map(x => x.result.marge));
              return (
                <div key={i} className={cn("p-3 rounded-lg border", isBest && i === 0 ? "border-orange-300 bg-orange-50" : "border-border bg-muted/20")}>
                  {isBest && i === 0 && <p className="text-[10px] font-bold text-red-600 uppercase mb-1">RECOMMANDÉ</p>}
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-bold">{r.scenario.label}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {r.scenario.sauts} sauts — {(r.scenario.gestes || []).map(g => GESTES_L[g] || g).join(", ")}
                        {(r.scenario.equipements || []).length > 0 && ` + ${r.scenario.equipements.map(e => EQUIP_L[e] || e).join(", ")}`}
                      </p>
                    </div>
                    <div className="flex gap-6 text-right shrink-0">
                      <div><p className="text-[10px] text-muted-foreground">CA TTC</p><p className="text-sm font-bold">{fmtC(r.result.caHT)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Coût</p><p className="text-sm font-bold text-red-500">{fmtC(-(r.result.coutTravaux || 0))}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Marge</p><p className={cn("text-sm font-bold", r.result.marge >= REGIE_THRESHOLD ? "text-emerald-600" : "text-red-500")}>{fmtC(r.result.marge)}</p></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Programme MPR Scénarios */}
      {resMPR.length > 0 && (
        <div className="border border-border rounded-xl bg-card p-4">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-500" /> Programme MPR — Scénarios
            <span className="text-[10px] text-muted-foreground font-normal">(régie {adminOptions[0]?.type === "cumule" ? "cumulée" : ""}: {fmtC(adminOptions[0]?.type === "cumule" ? regies.rMPRCum : regies.rMPR)})</span>
          </h3>
          <div className="space-y-3 mt-3">
            {resMPR.map((r, i) => {
              const isBest = r.result.marge === Math.max(...resMPR.map(x => x.result.marge));
              return (
                <div key={i} className={cn("p-3 rounded-lg border", isBest ? "border-blue-500/40 bg-blue-500/10" : "border-border bg-muted/20")}>
                  {isBest && <p className="text-[10px] font-bold text-blue-600 uppercase mb-1">RECOMMANDÉ</p>}
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-bold">{r.type}</p>
                    <div className="flex gap-6 text-right">
                      <div><p className="text-[10px] text-muted-foreground">MPR</p><p className="text-sm font-bold">{fmtC(r.result.mpr)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">CEE</p><p className="text-sm font-bold">{fmtC(r.result.cee)}</p></div>
                      <div><p className="text-[10px] text-muted-foreground">Marge</p><p className={cn("text-sm font-bold", r.result.marge >= REGIE_THRESHOLD ? "text-emerald-600" : "text-red-500")}>{fmtC(r.result.marge)}</p></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Timeline */}
      {adminTL.timelines.length > 0 && <TimelineDisplay timelines={adminTL.timelines} totalDays={adminTL.totalDays} />}
    </div>
  );

  // ═══ RENDER ═══
  return (
    <div className="space-y-5" data-testid="etude-detail">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-muted" data-testid="btn-back"><ArrowLeft className="w-4 h-4" /></button>
        <div>
          <h1 className="text-xl font-heading font-bold">{b.nom || "Étude"} — #{etude.id?.slice(-6)}</h1>
          <p className="text-xs text-muted-foreground">{fmtDT(etude.created_at)} — {etude.source}</p>
        </div>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="infos" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="infos" data-testid="tab-infos">Informations saisies</TabsTrigger>
            <TabsTrigger value="regie" data-testid="tab-regie">Vue Régie</TabsTrigger>
            <TabsTrigger value="finance" data-testid="tab-finance">Analyse financière</TabsTrigger>
          </TabsList>
          <TabsContent value="infos" className="mt-4">{renderInfoTab()}</TabsContent>
          <TabsContent value="regie" className="mt-4">{renderRegieTab()}</TabsContent>
          <TabsContent value="finance" className="mt-4">{renderFinanceTab()}</TabsContent>
        </Tabs>
      ) : (
        <Tabs defaultValue="infos" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="infos" data-testid="tab-infos">Informations saisies</TabsTrigger>
            <TabsTrigger value="resultats" data-testid="tab-resultats">Résultats</TabsTrigger>
          </TabsList>
          <TabsContent value="infos" className="mt-4">{renderInfoTab()}</TabsContent>
          <TabsContent value="resultats" className="mt-4">{renderRegieTab()}</TabsContent>
        </Tabs>
      )}

      {/* Régie actions */}
      {isRegie && (
        <div className="flex items-center gap-3 flex-wrap pt-4 border-t border-border">
          <button onClick={() => setShowRdvModal(true)} className="px-5 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm shadow" data-testid="btn-etude-rdv">
            <CalendarPlus className="w-4 h-4 inline mr-2" /> Planifier un RDV Admin
          </button>
          <button onClick={handleCreateDossierFromEtude} disabled={creatingDossier} className="px-5 py-2.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-semibold text-sm shadow disabled:opacity-50" data-testid="btn-etude-dossier">
            <FolderPlus className="w-4 h-4 inline mr-2" /> {creatingDossier ? "Création..." : "Créer un dossier"}
          </button>
        </div>
      )}

      <RdvAdminModal
        open={showRdvModal}
        onClose={() => setShowRdvModal(false)}
        defaultValues={{ nom: b.nom, telephone: b.telephone, email: b.email, adresse: b.adresse, programme: etude.recommendation === "cumule" ? "Cumulée" : etude.recommendation === "174" ? "174" : "MPR" }}
        etudeId={id}
        onCreated={(dossierId) => {
          toast.success("RDV planifié et dossier créé !");
          if (dossierId) navigate(`/dossier-regie/${dossierId}`);
        }}
      />
    </div>
  );
}
